import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    CcRequirementHistory,
    CcRequirementHistoryCreate,
    CcRequirementHistoryVersion,
    CcRequirementTempAnswer,
    MtDriveVersion,
} from "server/models"

import {
    APPROVED,
    OPEN,
    VERIFICATION,
    WORKFLOW_RERUN,
    VIEW,
} from "const"
import { CcStateSettingsType, hasProjectAccess } from "client/utils"
import {
    PRM_CC_DRAFTS,
    StoreType,
    setCcRequirementReloadAnswer,
    setCcRequirementTempAnswerHistory,
    initCcRequirementHistory,
} from "client/store"
import { PageForm } from "client/components"

import Form from "./form"

const Answer = () => {
    const revisionActiveList = useSelector((state: StoreType) => state.ccRequirement.revisionActiveList)
    const requirementDetails = useSelector((state: StoreType) => state.ccRequirement.requirementDetails)
    const requirementState = useSelector((state: StoreType) => state.ccRequirement.requirementState)
    const requirementHistory = useSelector((state: StoreType) => state.ccRequirement.requirementHistory)
    const tempAnswerByUser = useSelector((state: StoreType) => state.ccRequirement.tempAnswers)
    const tempAnswerVersionsByUser = useSelector((state: StoreType) => state.ccRequirement.tempAnswerVersions)
    const reloadAnswer = useSelector((state: StoreType) => state.ccRequirement.reloadAnswer)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[3] || []

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)

    const moduleResponseList = useSelector((state: StoreType) => state.ccResponse.moduleResponseList)
    const responseIds = moduleResponseList[4]
    const responsibleResponseIds = moduleResponseList[0]

    const userData = useSelector((state: StoreType) => state.system.userData)
    const dispatch = useDispatch()

    const [newHistoryCreate, setNewHistoryCreate] = useState<CcRequirementHistoryCreate>(null)
    const [newHistoryVersions, setNewHistoryVersions] = useState<MtDriveVersion[]>([])

    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    useEffect(() => {
        const { userId } = userData
        const { levelId, verifierId } = requirementState || {}
        const settings: CcStateSettingsType = requirementState && requirementState.settings
            ? JSON.parse(requirementState.settings)
            : {}
        const revision = revisionActiveList[requirementDetails.id]
        const isFirstLevel = levelId === levelIds[0]

        let lastHistory: CcRequirementHistory = null
        let newHistory: CcRequirementHistoryCreate = null
        let selectedLevelId: number = null
        let selectedUserId: number = null
        let userIds: readonly number[] = []
        let groupId: number = null
        let tempAnswer: CcRequirementTempAnswer = null
        let tempAnswerHistory: CcRequirementHistory = null

        // get the last history row made by user, not by 'system'
        for (let i = requirementHistory.length - 1; i >= 0; i--) {
            const history = requirementHistory[i]
            if (history.workflow === WORKFLOW_RERUN) {
                break
            }
            if (history.systemMessage === false) {
                lastHistory = history
                break
            }
        }

        let nextHistoryRow = true
        if (lastHistory && lastHistory.nextStatus === APPROVED) nextHistoryRow = false
        if (nextHistoryRow) {
            if (levelId && verifierId === userId) {
                // define users on assigned approval level
                groupId = levelList[levelId] ? levelList[levelId].groupId : null
                userIds = (groupUserList && groupUserList[groupId]) || []
                tempAnswer = tempAnswerByUser[userId] || null

                // if current user is assigned to approval level
                if (userIds.includes(userId)) {
                    // define next approval level and users
                    for (let i = 0; i < levelIds.length; i++) {
                        if (levelIds[i] === levelId && levelIds[i + 1]) {
                            selectedLevelId = levelIds[i + 1]
                            break
                        }
                    }
                    groupId = levelList[selectedLevelId] ? levelList[selectedLevelId].groupId : null
                    userIds = (groupUserList && groupUserList[groupId]) || []

                    if (settings[selectedLevelId]) {
                        selectedUserId = settings[selectedLevelId]
                        if (!userIds.includes(selectedUserId)) selectedUserId = null
                    }

                    newHistory = {
                        requirementId: requirementDetails.id,
                        requirementRevisionId: revision ? revision.id : null,
                        parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                        prevLevelId: levelId,
                        prevUserId: userId,
                        nextLevelId: selectedLevelId,
                        nextUserId: tempAnswer ? tempAnswer.nextUserId : selectedUserId,
                        responseId: tempAnswer ? tempAnswer.responseId : (
                            isFirstLevel
                                ? (responsibleResponseIds && responsibleResponseIds[0]) || null
                                : (responseIds && responseIds[0]) || null
                        ),
                        remark: tempAnswer ? tempAnswer.remark : "",
                        prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                        nextStatus: VERIFICATION,
                        workflow: null,
                        systemMessage: false,
                        editorId: null,
                    }
                }
            }

            // add tempAnswer to history
            if (
                levelId && verifierId
                && verifierId !== userId
                && tempAnswerByUser[verifierId]
                && viewDraftsAccess
            ) {
                tempAnswer = tempAnswerByUser[verifierId]
                // define next approval level
                for (let i = 0; i < levelIds.length; i++) {
                    if (levelIds[i] === levelId && levelIds[i + 1]) {
                        selectedLevelId = levelIds[i + 1]
                        break
                    }
                }

                const tempAnswerHistoryVersions: CcRequirementHistoryVersion[] = []
                if (tempAnswerVersionsByUser && tempAnswerVersionsByUser[verifierId]) {
                    tempAnswerVersionsByUser[verifierId].forEach((row) => {
                        if (row.version) {
                            tempAnswerHistoryVersions.push({
                                requirementHistoryId: -1,
                                versionId: row.version.id,
                                version: row.version,
                            })
                        }
                    })
                }

                tempAnswerHistory = {
                    ...initCcRequirementHistory,
                    id: -1,
                    parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                    prevLevelId: levelId,
                    prevUserId: verifierId,
                    nextLevelId: selectedLevelId,
                    nextUserId: tempAnswer.nextUserId || null,
                    responseId: tempAnswer.responseId || null,
                    remark: tempAnswer.remark || "",
                    prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                    nextStatus: VERIFICATION,
                    systemMessage: false,

                    versions: tempAnswerHistoryVersions,
                }
            }
        }

        const versions: MtDriveVersion[] = []
        if (tempAnswerVersionsByUser && tempAnswerVersionsByUser[userId]) {
            tempAnswerVersionsByUser[userId].forEach((row) => {
                if (row.version) versions.push(row.version)
            })
        }

        setNewHistoryCreate(newHistory)
        setNewHistoryVersions(versions)
        dispatch(setCcRequirementTempAnswerHistory(tempAnswerHistory))

        dispatch(setCcRequirementReloadAnswer(false))
        setTimeout(() => {
            dispatch(setCcRequirementReloadAnswer(true))
        }, 0)
    }, [
        requirementDetails,
        requirementState,
        requirementHistory,
        revisionActiveList,
        tempAnswerByUser,
        tempAnswerVersionsByUser,
        levelList,
        moduleLevelList,
        levelIds,
        responseIds,
        responsibleResponseIds,
    ])

    if (!newHistoryCreate) return null

    return (
        <PageForm noPaddingTop>
            {
                !reloadAnswer
                && <Form history={newHistoryCreate} historyVersions={newHistoryVersions} />
            }
            {
                reloadAnswer
                && <Form history={newHistoryCreate} historyVersions={newHistoryVersions} />
            }
        </PageForm>
    )
}
export default Answer
